<template>
	<div v-if="shouldShowTabs" class="page-tabs-container">
		<div class="page-tabs-wrapper">
			<div class="tabs-scrollbar">
				<div class="tabs-scrollbar-wrap">
					<div class="page-tabs">
						<div v-for="(route, index) in visitedRoutes" :key="route.fullPath"
							:class="['page-tab-item', { 'active': isActiveTab(route) }]" @click="switchTab(route)">
							<div class="tab-icon">
								<i class="el-icon-document"></i>
							</div>
							<span class="tab-title">{{ getTabTitle(route) }}</span>
							<!-- 只有一个标签时不显示关闭按钮 -->
							<div v-if="visitedRoutes.length > 1" class="tab-close-wrapper"
								@click.stop="closeTab(index, route)">
								<div class="tab-close">
									<i class="el-icon-close"></i>
								</div>
							</div>

							<!-- 添加小竖线分隔符，最后一个标签不显示 -->
							<div v-if="index < visitedRoutes.length - 1" class="tab-divider"></div>
						</div>
					</div>
				</div>
			</div>

			<!-- 标签操作按钮 - 只有一个标签时隐藏 -->
			<div class="tab-actions" v-if="visitedRoutes.length > 1">
				<el-dropdown trigger="click" @command="handleTabAction">
					<div class="tab-action-btn">
						<i class="el-icon-more"></i>
					</div>
					<template slot="dropdown">
						<el-dropdown-menu>
							<el-dropdown-item command="closeCurrent">
								<i class="el-icon-close"></i>
								<span>关闭当前</span>
							</el-dropdown-item>
							<el-dropdown-item command="closeOther">
								<i class="el-icon-copy-document"></i>
								<span>关闭其他</span>
							</el-dropdown-item>
							<el-dropdown-item command="closeAll">
								<i class="el-icon-delete"></i>
								<span>关闭全部</span>
							</el-dropdown-item>
							<el-dropdown-item divided command="refresh">
								<i class="el-icon-refresh"></i>
								<span>刷新当前</span>
							</el-dropdown-item>
						</el-dropdown-menu>
					</template>
				</el-dropdown>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		name: 'PageTabs',
		props: {
			// 当前路由对象，用于判断哪个标签是激活状态
			currentRoute: {
				type: Object,
				required: true
			},
			// 是否显示标签栏
			showTabs: {
				type: Boolean,
				default: true
			}
		},
		data() {
			return {
				visitedRoutes: [] // 访问过的页面路由记录
			}
		},
		computed: {
			shouldShowTabs() {
				// 始终显示标签栏，即使只有一个标签
				return this.showTabs && this.visitedRoutes.length > 0;
			}
		},
		watch: {
			currentRoute: {
				immediate: true,
				handler(to) {
					if (to) {
						this.addVisitedRoute(to); // 路由变化时添加访问记录
					}
				}
			}
		},
		methods: {
			// 页面标签栏相关方法
			ensureCurrentRouteInTabs(route) {
				// 确保当前路由在标签栏中（如果不在则添加）
				const exists = this.visitedRoutes.some(item =>
					item.fullPath === route.fullPath
				);

				if (!exists) {
					this.addVisitedRoute(route);
				}
			},

			addVisitedRoute(route) {
				// 如果是首页且没有meta信息，可以跳过
				if (route.path === '/' && (!route.meta || Object.keys(route.meta).length === 0)) {
					return;
				}

				// 检查是否已存在相同的路由
				const exists = this.visitedRoutes.some(item =>
					item.fullPath === route.fullPath
				);

				// 添加新的路由记录
				if (!exists) {
					const routeInfo = {
						fullPath: route.fullPath,
						path: route.path,
						meta: route.meta || {},
						name: route.name,
						query: route.query,
						params: route.params
					};

					this.visitedRoutes.push(routeInfo);
				}

				// 限制最大标签数量（例如最多10个）
				if (this.visitedRoutes.length > 10) {
					// 保留当前路由，移除最旧的非当前路由
					const currentIndex = this.visitedRoutes.findIndex(
						r => r.fullPath === route.fullPath
					);

					if (currentIndex > 0) {
						// 当前路由不是第一个，移除第一个
						this.visitedRoutes.splice(0, 1);
					} else if (currentIndex === -1) {
						// 当前路由不在数组中（这种情况不应该发生），移除第一个
						this.visitedRoutes.shift();
					}
					// 如果当前路由是第一个，不移除任何路由
				}
			},

			getTabTitle(route) {
				// 获取标签标题
				if (route.meta && route.meta.title) {
					return route.meta.title;
				}
				if (route.name) {
					return route.name;
				}
				// 从path中提取标题
				if (route.path && route.path !== '/') {
					const pathParts = route.path.split('/');
					const lastPart = pathParts[pathParts.length - 1];
					if (lastPart && lastPart !== 'index') {
						return lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
					}
				}
				return '首页';
			},

			isActiveTab(route) {
				// 检查是否是当前激活的标签
				return this.currentRoute.fullPath === route.fullPath;
			},

			switchTab(route) {
				// 切换到指定标签
				if (this.currentRoute.fullPath !== route.fullPath) {
					this.$router.push({
						path: route.fullPath,
						query: route.query,
						params: route.params
					});
				}
			},

			closeTab(index, route) {
				// 只有一个标签时不能关闭
				if (this.visitedRoutes.length <= 1) {
					return;
				}

				// 从数组中移除
				this.visitedRoutes.splice(index, 1);

				// 如果关闭的是当前激活的标签，跳转到前一个标签
				if (this.currentRoute.fullPath === route.fullPath) {
					const newIndex = Math.min(index, this.visitedRoutes.length - 1);
					if (newIndex >= 0) {
						this.switchTab(this.visitedRoutes[newIndex]);
					} else {
						// 如果没有其他标签，跳转到首页并确保首页在标签栏中
						this.$router.push('/');
					}
				}
			},

			handleTabAction(command) {
				switch (command) {
					case 'closeCurrent':
						// 关闭当前标签（只有一个标签时不能关闭）
						if (this.visitedRoutes.length <= 1) {
							this.$message.warning('至少需要保留一个标签页');
							return;
						}

						const currentIndex = this.visitedRoutes.findIndex(
							route => route.fullPath === this.currentRoute.fullPath
						);
						if (currentIndex >= 0) {
							this.closeTab(currentIndex, this.visitedRoutes[currentIndex]);
						}
						break;

					case 'closeOther':
						// 关闭其他标签，但确保至少保留一个
						if (this.visitedRoutes.length <= 1) {
							return;
						}

						const currentRoute = this.visitedRoutes.find(
							route => route.fullPath === this.currentRoute.fullPath
						);

						if (currentRoute) {
							this.visitedRoutes = [currentRoute];
						}
						break;

					case 'closeAll':
						// 关闭所有标签，但跳转到首页
						this.visitedRoutes = [];
						this.$router.push('/');
						break;

					case 'refresh':
						// 刷新当前页面
						this.$emit('refresh');
						break;
				}
			},

			// 对外提供的方法
			initVisitedRoutes(route) {
				// 初始化访问记录
				if (route) {
					this.ensureCurrentRouteInTabs(route);
				}
			},

			getVisitedRoutes() {
				return [...this.visitedRoutes];
			},

			setVisitedRoutes(routes) {
				this.visitedRoutes = [...routes];
			}
		}
	};
</script>

<style lang="scss" scoped>
	// 页面标签栏样式 - 极简现代风格
	.page-tabs-container {
		background: #ffffff;
		flex-shrink: 0;
		height: 48px;
		border-bottom: 1px solid #f0f0f0;

		.page-tabs-wrapper {
			display: flex;
			height: 100%;
			align-items: center;
			padding: 0 24px;

			.tabs-scrollbar {
				flex: 1;
				height: 100%;
				overflow: hidden;
				position: relative;

				.tabs-scrollbar-wrap {
					height: 100%;
					display: flex;
					align-items: center;
					overflow-x: auto;
					overflow-y: hidden;
					white-space: nowrap;
					-webkit-overflow-scrolling: touch;

					// 隐藏所有滚动条
					&::-webkit-scrollbar {
						display: none;
					}

					& {
						-ms-overflow-style: none;
						scrollbar-width: none;
					}
				}
			}

			.page-tabs {
				display: flex;
				align-items: center;
				height: 100%;
				gap: 2px; // 保留原来的间隙

				.page-tab-item {
					display: flex;
					align-items: center;
					height: 40px;
					padding: 0 16px;
					border-radius: 8px; // 保持原来的圆角
					cursor: pointer;
					transition: all 0.2s ease;
					flex-shrink: 0;
					max-width: 200px;
					min-width: 100px;
					position: relative;

					&:hover {
						background: #f8f9fa;

						// 只有一个标签时不显示关闭按钮的悬停效果
						&:not(:only-child) .tab-close-wrapper {
							opacity: 1;
						}

						.tab-title {
							color: #1890ff;
						}

						.tab-icon {
							color: #1890ff;
						}
					}

					&.active {
						background-color: #e8f3ff;

						.tab-icon {
							color: #2280fd;
						}

						.tab-title {
							color: #2280fd;
							font-weight: 500;
						}

						// 只有一个标签时不显示关闭按钮
						&:not(:only-child) .tab-close-wrapper {
							opacity: 1;

							.tab-close {
								color: #2280fd;

								&:hover {
									background: rgba(255, 255, 255, 0.7);
									color: #2280fd;
								}
							}
						}

						&::before {
							content: '';
							position: absolute;
							bottom: -1px;
							left: 0;
							right: 0;
							height: 2px;
							background: linear-gradient(90deg, transparent 0%, #ffffff 50%, transparent 100%);
							opacity: 0.5;
						}
					}

					.tab-icon {
						margin-right: 8px;
						font-size: 14px;
						color: #8c8c8c;
						transition: color 0.2s ease;
						display: flex;
						align-items: center;
					}

					.tab-title {
						flex: 1;
						font-size: 13px;
						color: #595959;
						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
						transition: color 0.2s ease;
					}

					.tab-close-wrapper {
						margin-left: 8px;
						opacity: 0;
						transition: opacity 0.2s ease;

						.tab-close {
							width: 18px;
							height: 18px;
							border-radius: 50%;
							display: flex;
							align-items: center;
							justify-content: center;
							font-size: 10px;
							color: #bfbfbf;
							transition: all 0.2s ease;
							cursor: pointer;

							&:hover {
								background: rgba(0, 0, 0, 0.06);
								color: #8c8c8c;
								transform: rotate(90deg);
							}

							i {
								font-weight: bold;
							}
						}
					}

					// 小竖线分隔符
					.tab-divider {
						position: absolute;
						right: -1px; // 放置在标签右侧，与gap对齐
						top: 50%;
						transform: translateY(-50%);
						width: 1px;
						height: 16px;
						background: #e8e8e8;
						z-index: 1;
					}
				}
			}

			.tab-actions {
				margin-left: 12px;
				flex-shrink: 0;

				.tab-action-btn {
					width: 32px;
					height: 32px;
					border-radius: 6px;
					display: flex;
					align-items: center;
					justify-content: center;
					cursor: pointer;
					color: #8c8c8c;
					font-size: 16px;
					transition: all 0.2s ease;

					&:hover {
						background: #f5f5f5;
						color: #595959;
					}
				}

				::v-deep .el-dropdown-menu {
					min-width: 140px;
					padding: 8px 0;

					.el-dropdown-menu__item {
						display: flex;
						align-items: center;
						font-size: 13px;
						padding: 8px 16px;
						color: #595959;

						i {
							margin-right: 10px;
							font-size: 14px;
							color: #8c8c8c;
							width: 16px;
							text-align: center;
						}

						span {
							flex: 1;
						}

						&:hover {
							background: #f5f5f5;
							color: #1890ff;

							i {
								color: #1890ff;
							}
						}

						&.el-dropdown-menu__item--divided {
							border-top: 1px solid #f0f0f0;
							margin-top: 8px;
							padding-top: 12px;
						}
					}
				}
			}
		}

		// 移动端样式
		@media (max-width: 768px) {
			height: 44px;

			.page-tabs-wrapper {
				padding: 0 16px;

				.page-tabs {
					.page-tab-item {
						height: 36px;
						padding: 0 12px;
						min-width: 80px;
						max-width: 160px;
						border-radius: 6px;

						.tab-icon {
							font-size: 12px;
							margin-right: 6px;
						}

						.tab-title {
							font-size: 12px;
						}

						.tab-close-wrapper {
							opacity: 0.5;
							margin-left: 6px;

							.tab-close {
								width: 16px;
								height: 16px;
								font-size: 9px;
							}
						}

						// 移动端分隔符
						.tab-divider {
							height: 14px;
						}
					}
				}

				.tab-actions {
					margin-left: 8px;

					.tab-action-btn {
						width: 28px;
						height: 28px;
						font-size: 14px;
					}
				}
			}
		}

		// 超小屏手机
		@media (max-width: 480px) {
			height: 40px;

			.page-tabs-wrapper {
				padding: 0 12px;

				.page-tabs {
					.page-tab-item {
						height: 32px;
						padding: 0 10px;
						min-width: 70px;
						max-width: 140px;
						border-radius: 4px;

						.tab-icon {
							display: none;
						}

						.tab-title {
							font-size: 11px;
						}

						.tab-close-wrapper {
							margin-left: 4px;

							.tab-close {
								width: 14px;
								height: 14px;
								font-size: 8px;
							}
						}

						// 超小屏分隔符
						.tab-divider {
							height: 12px;
						}
					}
				}

				.tab-actions {
					display: none;
				}
			}
		}
	}
</style>